import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PageHeaderComponent } from '../../../components/page-header/page-header.component';
import { StatusKind, StatusPillComponent } from '../../../components/status-pill/status-pill.component';
import { ProjectNavComponent } from '../../../projects/project-nav/project-nav.component';
import { ProjectRoleService } from '../../../services/project-role.service';
import { ObjectCaptureService } from '../../../services/object-capture.service';
import {
  CaptureAcceptItem,
  CaptureAcceptResult,
  CaptureLocator,
  CaptureView,
} from '../../../models/object-capture.model';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { defaultName, isPassword, pillOf } from '../capture-rows';
import { isValidObjectName } from '../object-names';
import { serverMessage } from '../../../shared/http/server-message';

type LoadState = 'loading' | 'ok' | 'error';

/** Warum der Name so nicht in die Bibliothek kann; `taken` und `conflict` lassen sich durch Ersetzen loesen. */
type NameIssue = 'invalid' | 'duplicate' | 'taken' | 'conflict' | null;

interface StatePill {
  kind: StatusKind;
  key: string;
  name?: string;
}

interface ReviewRow {
  capture: CaptureView;
  id: string;
  name: string;
  selected: boolean;
  /** Nicht gestrichene Kandidaten; `index` ist die Position in `capture.locators`. */
  candidates: { index: number; locator: CaptureLocator }[];
  issue: NameIssue;
  replace: boolean;
  pill: StatePill | null;
  showImage: boolean;
}

/**
 * Seite „Abnehmen“ (NEX-1722): offene Aufnahmen einer Objektbibliothek pruefen und uebernehmen.
 *
 * Design-Luecke: Das Bundle kennt keine Abnahme. Kopf und Karte folgen page-testschritte.jsx (186-327),
 * die Hinweisflaeche page-testlauf.jsx (496-516), Zeilen und Kandidaten dem Stil der Objektbibliothek.
 */
@Component({
  selector: 'app-capture-review',
  imports: [
    TranslatePipe,
    RouterLink,
    MatButton,
    MatCheckbox,
    MatIcon,
    MatProgressSpinner,
    PageHeaderComponent,
    StatusPillComponent,
    ProjectNavComponent,
  ],
  templateUrl: './capture-review.component.html',
  styleUrl: './capture-review.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaptureReviewComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly captureService = inject(ObjectCaptureService);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  readonly isReadOnly = inject(ProjectRoleService).isReadOnly;
  readonly fieldLengths = FIELD_LENGTHS;

  readonly projectId: string = this.route.snapshot.params['projectId'];
  readonly mappingId: string = this.route.snapshot.params['mappingId'];
  readonly libraryLink = ['/projects', this.projectId, 'object-mappings', this.mappingId];

  readonly state = signal<LoadState>('loading');
  readonly accepting = signal(false);
  /** `message` ist null, wenn der Server keinen zeigbaren Text liefert; dann gilt der Standardtext. */
  readonly acceptError = signal<{ message: string | null } | null>(null);
  readonly result = signal<CaptureAcceptResult | null>(null);

  private readonly captures = signal<CaptureView[]>([]);
  private readonly selected = signal<ReadonlySet<string>>(new Set());
  private readonly names = signal<ReadonlyMap<string, string>>(new Map());
  private readonly removed = signal<ReadonlyMap<string, ReadonlySet<number>>>(new Map());
  private readonly replaced = signal<ReadonlySet<string>>(new Set());
  /** Name, mit dem die Aufnahme beim letzten Uebernehmen in einen Konflikt lief. */
  private readonly conflicts = signal<ReadonlyMap<string, string>>(new Map());
  private readonly expanded = signal<ReadonlySet<string>>(new Set());
  private readonly imageUrls = signal<ReadonlyMap<string, string>>(new Map());
  private loadRequest: Subscription | null = null;

  readonly rows = computed<ReviewRow[]>(() => {
    const selected = this.selected();
    const names = this.names();
    const removed = this.removed();
    const replaced = this.replaced();
    const conflicts = this.conflicts();
    const nameCounts = new Map<string, number>();
    for (const c of this.captures()) {
      if (!selected.has(c.id)) continue;
      const name = (names.get(c.id) ?? '').trim();
      nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    }
    return this.captures().map((capture) => {
      const name = names.get(capture.id) ?? '';
      const trimmed = name.trim();
      const isSelected = selected.has(capture.id);
      const gone = removed.get(capture.id) ?? new Set<number>();
      const candidates = capture.locators
        .map((locator, index) => ({ index, locator }))
        .filter((c) => !gone.has(c.index));
      const issue = nameIssue(
        capture,
        trimmed,
        isSelected && (nameCounts.get(trimmed) ?? 0) > 1,
        conflicts.get(capture.id),
      );
      return {
        capture,
        id: capture.id,
        name,
        selected: isSelected,
        candidates,
        issue,
        replace: replaced.has(capture.id) && (issue === 'taken' || issue === 'conflict'),
        pill: statePill(capture, issue),
        showImage: capture.hasImage && !isPassword(capture),
      };
    });
  });

  readonly selectedCount = computed(() => this.rows().filter((r) => r.selected).length);
  readonly allSelected = computed(() => this.rows().length > 0 && this.selectedCount() === this.rows().length);
  private readonly positionOnlyRows = computed(() =>
    this.rows().filter((r) => pillOf(r.capture.locators) === 'positionOnly'),
  );
  readonly positionHintKey = computed(() => {
    const rows = this.positionOnlyRows();
    if (rows.length === 0) return null;
    return rows.every((r) => !r.selected)
      ? 'objectMappings.review.positionHint'
      : 'objectMappings.review.positionHintSelected';
  });
  readonly countKey = computed(() =>
    this.rows().length === 1 ? 'objectMappings.review.countOne' : 'objectMappings.review.count',
  );
  readonly resultText = computed(() => {
    const result = this.result();
    if (!result) return null;
    const params = {
      created: result.created.length,
      updated: result.updated.length,
      unchanged: result.unchanged.length,
    };
    if (params.unchanged === 0) return { key: 'objectMappings.review.result', params };
    const onlyUnchanged = params.created === 0 && params.updated === 0;
    return {
      key: onlyUnchanged ? 'objectMappings.review.resultOnlyUnchanged' : 'objectMappings.review.resultWithUnchanged',
      params,
    };
  });
  readonly summary = computed(() => {
    const rows = this.rows();
    const chosen = rows.filter((r) => r.selected);
    return {
      added: chosen.filter((r) => !r.replace).length,
      replaced: chosen.filter((r) => r.replace).length,
      discarded: rows.length - chosen.length,
    };
  });
  readonly canAccept = computed(
    () =>
      !this.accepting() &&
      this.rows().length > 0 &&
      this.rows().every((r) => !r.selected || r.issue === null || r.replace),
  );

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.loadRequest?.unsubscribe();
      this.imageUrls().forEach((url) => URL.revokeObjectURL(url));
    });
  }

  ngOnInit(): void {
    this.load();
  }

  retry(): void {
    this.state.set('loading');
    this.load();
  }

  imageUrl(id: string): string | null {
    return this.imageUrls().get(id) ?? null;
  }

  isExpanded(id: string): boolean {
    return this.expanded().has(id);
  }

  toggleExpanded(id: string): void {
    this.expanded.update((set) => toggled(set, id));
  }

  toggleSelected(id: string): void {
    this.selected.update((set) => toggled(set, id));
  }

  toggleAll(): void {
    this.selected.set(this.allSelected() ? new Set() : new Set(this.captures().map((c) => c.id)));
  }

  discard(id: string): void {
    this.selected.update((set) => without(set, id));
  }

  onNameInput(id: string, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.names.update((names) => new Map(names).set(id, value));
    this.replaced.update((set) => without(set, id));
  }

  replace(id: string): void {
    this.replaced.update((set) => new Set(set).add(id));
  }

  rename(id: string): void {
    this.replaced.update((set) => without(set, id));
    this.host.nativeElement.querySelector<HTMLInputElement>(`#${nameFieldId(id)}`)?.focus();
  }

  nameFieldId(id: string): string {
    return nameFieldId(id);
  }

  removeCandidate(row: ReviewRow, index: number): void {
    if (row.candidates.length <= 1) return;
    this.removed.update((map) => new Map(map).set(row.id, new Set(map.get(row.id) ?? []).add(index)));
  }

  accept(): void {
    if (!this.canAccept()) return;
    const rows = this.rows();
    const accept: CaptureAcceptItem[] = rows
      .filter((r) => r.selected)
      .map((r) => ({
        captureId: r.id,
        logicalName: r.name.trim(),
        locators: r.candidates.map((c) => c.locator),
        typeName: r.capture.typeName,
        replace: r.replace,
      }));
    const discard = rows.filter((r) => !r.selected).map((r) => r.id);
    this.accepting.set(true);
    this.acceptError.set(null);
    this.captureService
      .accept(this.projectId, this.mappingId, { accept, discard })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.accepting.set(false);
          this.applyResult(result, accept);
        },
        error: (err: unknown) => {
          this.accepting.set(false);
          this.acceptError.set({ message: serverMessage(err) });
        },
      });
  }

  private applyResult(result: CaptureAcceptResult, sent: CaptureAcceptItem[]): void {
    const done = new Set([...result.acceptedCaptureIds, ...result.discardedCaptureIds]);
    const conflictNames = new Set(result.conflicts.map((c) => c.logicalName));
    const conflicts = new Map(this.conflicts());
    for (const item of sent) {
      if (conflictNames.has(item.logicalName)) conflicts.set(item.captureId, item.logicalName);
    }
    this.result.set(result);
    this.conflicts.set(conflicts);
    this.replaced.set(new Set());
    this.captures.update((list) => list.filter((c) => !done.has(c.id)));
  }

  private load(): void {
    this.loadRequest?.unsubscribe();
    this.loadRequest = this.captureService.list(this.projectId, this.mappingId).subscribe({
      next: (captures) => {
        this.captures.set(captures);
        this.names.set(new Map(captures.map((c) => [c.id, defaultName(c) ?? ''])));
        this.selected.set(new Set(captures.filter((c) => pillOf(c.locators) !== 'positionOnly').map((c) => c.id)));
        captures.forEach((c) => this.loadImage(c));
        this.state.set('ok');
      },
      error: () => this.state.set('error'),
    });
  }

  private loadImage(capture: CaptureView): void {
    if (!capture.hasImage || isPassword(capture) || this.imageUrls().has(capture.id)) return;
    this.captureService
      .image(this.projectId, this.mappingId, capture.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          this.imageUrls.update((urls) => new Map(urls).set(capture.id, url));
        },
        error: () => undefined,
      });
  }
}

/** Ids der Aufnahmen sind UUIDs; mit Praefix taugen sie als Element-Id. */
function nameFieldId(captureId: string): string {
  return `cr-name-${captureId}`;
}

function nameIssue(
  capture: CaptureView,
  name: string,
  duplicateInList: boolean,
  conflictName: string | undefined,
): NameIssue {
  if (!isValidObjectName(name)) return 'invalid';
  if (duplicateInList) return 'duplicate';
  if (conflictName === name) return 'conflict';
  if (capture.nameTaken && name === capture.suggestedName) return 'taken';
  return null;
}

/** Genau eine Pille je Zeile: Name doppelt vor gleichem Element vor nur Position vor stabil. */
function statePill(capture: CaptureView, issue: NameIssue): StatePill | null {
  if (issue === 'duplicate' || issue === 'taken' || issue === 'conflict') {
    return { kind: 'pending', key: 'nameDuplicate' };
  }
  if (capture.duplicateOf) return { kind: 'laufend', key: 'sameElement', name: capture.duplicateOf };
  const pill = pillOf(capture.locators);
  if (pill === 'positionOnly') return { kind: 'pending', key: 'positionOnly' };
  if (pill === 'stable') return { kind: 'aktiv', key: 'stable' };
  return null;
}

function toggled(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(set);
  if (!next.delete(id)) next.add(id);
  return next;
}

function without(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(set);
  next.delete(id);
  return next;
}
